import _ from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {FmlxButton, FmlxComboBox, FmlxDropdown, FmlxIcon, FmlxTooltip} from 'fmlx-common-ui';
import {useManageAssignmentContext} from '../../../lib/contexLib';
import {ALERT_VARIANT, RESPONSE_STATUS, REVIEW_TYPE} from '../../../const/dataEnum';
import {FMLX_BUTTON, FMLX_COMPONENT} from '../../../const/FmlxComponentEnum';
import {setAlert} from '../../../stores/actions/alertModalStateAction';
import manageAssignmentBusiness from '../../../stores/business/manageAssignmentBusiness';
import projectBusiness from '../../../stores/business/projectBusiness';
import './ReviewedByTab.scss';
import employeeApi from '../../../api/rest/employeeApi';
import employeeReviewApi from '../../../api/rest/employeeReviewApi';
import EssSimpleTable from '../../../components/MessageTable/TableColumn/EssSimpleTable';

const tabIndex = 1;
const ReviewedByTab = () => {
  const dispatch = useDispatch();
  const {selectedTab, periodId, departmentId, practiceId, projectId, employeeName, period} =
    useManageAssignmentContext();

  const [dataState, setDataState] = useState([]);
  const [responseStatus, setResponseStatus] = useState(RESPONSE_STATUS.IDLE);
  const [totalItem, setTotalItem] = useState();
  const [pageIndexState, setPageIndexState] = useState(0);
  const [pageSizeState, setPageSizeState] = useState(10);
  const [pageNoState, setPageNoState] = useState(0);
  const [employeeList, setEmployeeList] = useState([]);
  const [editReviewList, setEditReviewList] = useState([]);
  const [sortState, setSortState] = useState({
    sortBy: 'fullName',
    sortType: 'asc',
  });
  const [loading, setLoading] = useState(false);
  const reviewTypeItem = Object.entries(REVIEW_TYPE).map(([key, val], i) => {
    return {
      id: i,
      text: val.Name,
      group: val,
    };
  });

  const pageSizes = [10, 20, 30, 40, 50];
  const shouldCallApiRef = useRef(true);
  const selectedRowItemIdRef = useRef(null);

  const onProjectChangeHandler = (reviewerId, selectedReviewIndex, revieweeProjectID) => {
    setEditReviewList((reviewList) => {
      const newReviewList = [...reviewList];
      const selectedReviewerIndex = newReviewList[selectedReviewIndex].data.findIndex((item) => item.id === reviewerId);
      newReviewList[selectedReviewIndex].data[selectedReviewerIndex].revieweeProjectID = revieweeProjectID;
      return newReviewList;
    });
  };

  const onEditRevieweeClickHandler = async (revieweeId, employee, reviewers) => {
    selectedRowItemIdRef.current = revieweeId;
    let newReviewers = [...reviewers];
    if (shouldCallApiRef.current) {
      setLoading(true);
      newReviewers = await dispatch(manageAssignmentBusiness.getReviewersWithRevieweeProject(revieweeId, newReviewers));
      setLoading(false);
      setEditReviewList([
        {
          id: revieweeId,
          employee,
          data: newReviewers,
        },
      ]);
      shouldCallApiRef.current = false;
    }
  };

  const columnData = [
    {
      id: 'fullName',
      accessor: 'fullName',
      Header: 'Reviewee Name',
      sortable: true,
      renderCell: (col, row) => row.fullName,
    },
    {
      id: 'departmentName',
      accessor: 'departmentName',
      Header: 'Department',
      sortable: true,
      renderCell: (col, row) => row.departmentName,
    },
    {
      id: 'projectName',
      accessor: 'projectName',
      Header: 'Reviewee Projects',
      sortable: true,
      renderCell: (col, row) => {
        const projects = row.projectName?.split(',');
        return projects ? (
          <>
            {projects.map((p, i) => (
              <div key={i}>
                {p}
              </div>
            ))}
          </>
        ) : (
          '-'
        );
      },
    },
    {
      id: 'reviewers',
      accessor: 'reviewers',
      Header: 'Reviewer Name',
      sortable: false,
      renderCell: (col, row) => {
        const value = row.reviewers;
        const revieweeId = row.id;
        let editState = editReviewList.find((data) => data.id === revieweeId);
        let editStateIndex = editReviewList.findIndex((data) => data.id === revieweeId);
        let reviewers = editState ? editState.data : value;
        const isRowSelected = revieweeId === selectedRowItemIdRef.current;
        const isButtonEditDisabled = (editReviewList.length > 0 && !editState) || loading;

        return (
          <>
            <div key={'reviewees' + revieweeId}>
              {reviewers.map((reviewer, i) => {
                const allowEdit = reviewer.reviewTypeName !== REVIEW_TYPE.CONS.Name && reviewer.fullName !== row.fullName;

                const selectedReviewerProjectId = projectBusiness.getProjectById(reviewer.projects, reviewer.revieweeProjectID)?.id;
                return (
                  <div
                    className={`reviewer-grid ${editState ? 'edit-state' : 'normal'}`}
                    key={reviewer.id + reviewer.reviewTypeID + i}
                  >
                    <div className="text-ellipsis">
                      <FmlxTooltip title={reviewer.fullName}>
                        <span>{reviewer.fullName}</span>
                      </FmlxTooltip>
                    </div>
                    <div>
                      {editState && reviewer.status !== 'done' && allowEdit && reviewer.projects.length > 0 ? (
                        <FmlxDropdown
                          size={FMLX_COMPONENT.SIZE.S}
                          items={reviewer.projects}
                          disabled={loading}
                          selectedItem={selectedReviewerProjectId}
                          onChange={(projectId) => onProjectChangeHandler(reviewer.id, editStateIndex, projectId)}
                        />
                      ) : null}
                    </div>
                    <div >
                      {editState && reviewer.status !== 'done' && allowEdit ? (
                        <FmlxDropdown
                          placeholder="Select Review Type"
                          items={reviewTypeItem.filter(
                            (rt) => rt.text !== REVIEW_TYPE.CONS.Name && rt.text !== REVIEW_TYPE.SELF.Name
                          )}
                          selectedItem={reviewTypeItem.find((rt) => rt.text === reviewer.reviewTypeName).id}
                          onChange={(itemIndex) => {
                            const newList = [...editReviewList];
                            const reviewerIndex = reviewers.findIndex((r) => r.id === reviewer.id);
                            const updatedReviewers = [...reviewers];
                            updatedReviewers[reviewerIndex] = {
                              ...updatedReviewers[reviewerIndex],
                              reviewTypeName: reviewTypeItem[itemIndex].text,
                              reviewTypeID: reviewTypeItem[itemIndex].group.Id,
                            };
                            newList[editStateIndex].data = updatedReviewers;
                            setEditReviewList(newList);
                          }}
                          size={FMLX_COMPONENT.SIZE.S}
                          disabled={loading}
                        />
                      ) : null}
                    </div>

                    {editState ? (
                      <div>
                        <FmlxButton
                          size={FMLX_COMPONENT.SIZE.M}
                          type={FMLX_BUTTON.TYPE.BASIC}
                          variant={FMLX_BUTTON.VARIANT.PLAIN}
                          disabled={reviewer.status === 'done' || !allowEdit || loading}
                          onlyIcon
                          icon={<FmlxIcon name="Trash" size={FMLX_COMPONENT.SIZE.M} />}
                          onClick={() => {
                            const newList = _.cloneDeep(editReviewList);
                            const idx = reviewers.findIndex((r) => r.id === reviewer.id);
                            newList[editStateIndex].data.splice(idx, 1);
                            setEditReviewList(newList);
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div className='reviewer-control' key={revieweeId + 'Action'}>
              {editState ? (
                <>
                  <div className='combo-box-wrapper'>
                    <FmlxComboBox
                      placeholder="Select Reviewer"
                      disabled={loading}
                      items={employeeList}
                      onChange={async (item) => {
                        if (!item) return;
                        const current = [...editReviewList];
                        if (current[editStateIndex].data.some((d) => d.id === item.id)) {
                          return dispatch(
                            setAlert({
                              show: true,
                              variant: ALERT_VARIANT.ERROR,
                              message: 'Reviewee already in the list',
                            })
                          );
                        }
                        setLoading(true);
                        const employeeProject = await manageAssignmentBusiness.getProjectByEmployeeId(revieweeId);
                        setLoading(false);
                        current[editStateIndex].data.push({
                          id: item.data.id,
                          fullName: item.data.fullName,
                          departmentID: item.data.departmentID,
                          practiceID: item.data.practiceID,
                          reviewTypeID: REVIEW_TYPE.PEER.Id,
                          reviewTypeName: REVIEW_TYPE.PEER.Name,
                          revieweeProjectID: employeeProject[0]?.project?.id,
                          projects: employeeProject.map((emp) => ({
                            id: emp.project.id,
                            text: emp.project.projectName,
                            group: '',
                          })),
                        });
                        setEditReviewList(current);
                      }}
                    />
                  </div>
                  <FmlxButton
                    size={FMLX_COMPONENT.SIZE.M}
                    type={FMLX_BUTTON.TYPE.DANGER}
                    variant={FMLX_BUTTON.VARIANT.CONTAIN}
                    disabled={loading}
                    onlyIcon
                    tooltipDescription="Cancel"
                    icon={<FmlxIcon name="CancelOutline" size={FMLX_COMPONENT.SIZE.M} />}
                    onClick={() => {
                      setEditReviewList([]);
                      shouldCallApiRef.current = true;
                    }}
                  />
                  <FmlxButton
                    showProgress={loading}
                    size={FMLX_COMPONENT.SIZE.M}
                    type={FMLX_BUTTON.TYPE.POSITIVE}
                    variant={FMLX_BUTTON.VARIANT.CONTAIN}
                    label="SAVE"
                    withIcon={FMLX_BUTTON.WITH_ICON.START}
                    icon={<FmlxIcon name="Save" size={FMLX_COMPONENT.SIZE.M} />}
                    onClick={() => saveReviewer(revieweeId, reviewers, editStateIndex)}
                  />
                </>
              ) : (
                period?.isActive && (
                  <FmlxButton
                    showProgress={loading && isRowSelected}
                    size={FMLX_COMPONENT.SIZE.M}
                    type={FMLX_BUTTON.TYPE.PRIMARY}
                    variant={FMLX_BUTTON.VARIANT.CONTAIN}
                    disabled={isButtonEditDisabled}
                    label="EDIT"
                    withIcon={FMLX_BUTTON.WITH_ICON.START}
                    icon={<FmlxIcon name="PencilBorder" size={FMLX_COMPONENT.SIZE.M} />}
                    onClick={() => onEditRevieweeClickHandler(revieweeId, row, value)}
                  />
                )
              )}
            </div>
          </>
        );
      },
    },
    {
      id: 'revieweeProjectName',
      accessor: '',
      Header: 'Reviewer Project',
      sortable: false,
      renderCell: (col, row) => {
        return (
          <div>
            {row.reviewers.map((r) => (
              <div key={`project-${r.id}`}>{r.revieweeProjectName}</div>
            ))}
          </div>
        );
      },
    },
    {
      id: 'reviewTypeName',
      accessor: '',
      Header: 'Relation',
      sortable: false,
      renderCell: (col, row) => {
        return (
          <div>
            {row.reviewers.map((r) => (
              <div key={`relation-${r.id}`}>{`(${r.reviewTypeName})`}</div>
            ))}
          </div>
        );
      },
    },
    {
      id: 'status',
      accessor: '',
      Header: 'Review Status',
      sortable: false,
      renderCell: (col, row) => {
        return (
          <div>
            {row.reviewers.map((r) => (
              <div key={`status-${r.id}`}>{r.status}</div>
            ))}
          </div>
        );
      },
    },
    {
      id: 'totalReviewers',
      accessor: 'totalReviewers',
      Header: 'Total',
      sortable: true,
      renderCell: (col, row) => row.totalReviewers,
    },
  ];

  const fetchEmployee = useCallback(async () => {
    const response = await employeeApi.getEmployee();
    try {
      const {data, responseCode} = response;
      if (responseCode === 0) {
        let emps = data.map((employee, index) => ({
          id: employee.id,
          text: employee.fullName,
          group: '',
          data: employee,
        }));
        setEmployeeList(emps);
      }
    } catch (error) {
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: error.message}));
    }
  }, [dispatch]);

  const getTableData = useCallback(
    async ({period, employee, practice, project, department, sort, pageNo, pageSize}) => {
      if (period !== 0 && department !== null) {
        setResponseStatus(RESPONSE_STATUS.PENDING);
        try {
          const response = await employeeReviewApi.getReviewerAssignment({
            periodId,
            employee,
            practiceId: practice,
            projectId: project,
            departmentId: department,
            sortBy: sort.sortBy,
            sortType: sort.sortType,
            pageNo,
            pageSize,
          });
          if (!response) return;
          const {data, responseCode, totalCount} = response;
          if (responseCode === 0) {
            setTotalItem(totalCount);
            setDataState(
              data.map((item) => ({
                ...item,
                reviewers: item.reviewers.map((reviewer) => ({
                  ...reviewer,
                  revieweeProjectID: reviewer?.revieweeProjectID ?? null,
                  projects: [],
                })),
              }))
            );
          } else if (responseCode === -10) {
            setTotalItem(0);
            setDataState([]);
          }
          setResponseStatus(RESPONSE_STATUS.SUCCESS);
      } catch (error) {
        dispatch(setAlert({ show: true, variant: ALERT_VARIANT.ERROR, message: error.message }));
        setResponseStatus(RESPONSE_STATUS.ERROR);
      }
    }
  },
    [periodId, dispatch] 
  );


  const fetchData = useCallback(
    ({pageSize, pageIndex}) => {
      setPageSizeState(pageSize);
      setPageIndexState(pageIndex);
      setPageNoState(pageIndex + 1);
      setEditReviewList([]);
      shouldCallApiRef.current = true;
      getTableData({
        period: periodId,
        employee: employeeName,
        practice: practiceId,
        project: projectId,
        department: departmentId,
        sort: sortState,
        pageNo: pageIndex + 1,
        pageSize,
      });
    },
    [getTableData, periodId, employeeName, practiceId, projectId, departmentId, sortState]
  );

  useEffect(() => {
    fetchEmployee();
  }, [fetchEmployee]);

  useEffect(() => {
    if (selectedTab === tabIndex) {
      fetchData({pageSize: pageSizeState, pageIndex: 0});
    }
  }, [selectedTab, fetchData]);

  const getSortValue = ({type, column}) => {
    setSortState({
      sortBy: column,
      sortType: type,
    });
  };

  const saveReviewer = async (reviewerID, data, editIndex) => {
    const dataparam = {
      id: reviewerID,
      reviewers: data.map((item) => ({
        id: item.id,
        departmentID: item.departmentID,
        fullName: item.fullName,
        practiceID: item.practiceID,
        reviewTypeID: item.reviewTypeID,
        reviewTypeName: item.reviewTypeName,
        status: item.status,
        revieweeProjectID: item?.revieweeProjectID,
      })),
    };
    const param = {
      reviewPeriod: periodId,
    };
    setLoading(true);
    try {
      const response = await employeeReviewApi.syncReviewer(dataparam, param)
      const {message, responseCode} = response;
      if (responseCode === 0) {
        setEditReviewList(_.cloneDeep(editReviewList).splice(editIndex + 1, 1));
        fetchData({pageSize: pageSizeState, pageIndex: pageIndexState});
        dispatch(setAlert({show: true, variant: ALERT_VARIANT.SUCCESS, message: message}));
      } else {
        dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: message}));
      }
    } catch (error) {
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: error.message}));
    } finally {
      setLoading(false);
      shouldCallApiRef.current = true;
    }
  };
  
  const memoizedDataSource = useMemo(() => dataState, [dataState]);

  const handleChangeRowsPerPage = (e) => {
    const newPageSize = parseInt(e.target.value, 10);
    const startIndex = pageIndexState * pageSizeState;
    const newPageIndex = Math.floor(startIndex / newPageSize);
    fetchData({pageSize: newPageSize, pageIndex: newPageIndex});
  };
  /**
   * https://kentcdodds.com/blog/stop-using-isloading-booleans
   * const isLoading = status === 'idle' || status === 'pending'
   * const isResolved = status === 'resolved'/'success'
   * const isRejected = status === 'rejected'/'error'
   */
  return (
    <EssSimpleTable
      columnData={columnData}
      columns={(col, row) => row[col.accessor]}
      dataCount={Array(totalItem).fill(0)}
      filteredData={memoizedDataSource}
      sortTableDirection={sortState.sortType}
      sortKey={sortState.sortBy}
      onSort={(column) => {
        const type = sortState.sortBy === column && sortState.sortType === 'asc' ? 'desc' : 'asc';
        getSortValue({type, column});
      }}
      onChangePage={(e, newPage) => fetchData({pageSize: pageSizeState, pageIndex: newPage})}
      onChangeRowsPerPage={handleChangeRowsPerPage}
      onPageOption={pageSizes}
      rowsPerPage={pageSizeState}
      page={pageIndexState}
      hideFooter={false}
      hideHeader={false}
      onEmptyDataShow="No data available"
      showDisplaySettings
    />
  );
};

export default ReviewedByTab;
